import { ref, nextTick } from 'vue';

import useSortFilter from './useSortFilter';
import useFacet from './useFacet';
import useFacetOptions from './useFacetOptions';
import MultiSelectRangeUtil from '../components/criteria/util/MultiSelectRangeUtil';

const { isCustomOption } = MultiSelectRangeUtil;

export default function useMultiSelectDefault(props) {
  const {
    replaceFacetSelectedValues,
    removeFacetCheckboxValuesByValue,
    removeCheckboxValuesByValue: removeStoreCheckboxValuesByValue,
    addFacetCheckboxValues,
    facet: facetData,
    isSlideoutMode,
    facetName,
    globalOptions,
  } = useFacet(props);

  const {
    removeCheckBoxValuesByValue,
    facetByName,
    updateFacet,
    addCheckboxValue,
  } = useSortFilter();

  const { focusActiveDropdown } = useFacetOptions();

  const customRange = ref({
    minVal: '',
    maxVal: '',
  });
  const customForm = ref(null);
  const minInput = ref(null);
  const maxInput = ref(null);

  function customValueRenderer(value) {
    return {
      ...value,
      isCustom: isCustomOption(value),
    };
  }

  const onUpdate = (selected, updatedFacetName, criteriaOptions) => {
    const customOption = criteriaOptions.find((v) => v.isCustom);
    const customValue = customOption?.value || null;
    let selectedValues = selected;

    /* istanbul ignore else */
    if (customValue !== null) {
      selectedValues = selected.filter((value) => value !== customValue);

      if (isSlideoutMode.value && !props.isDynamic) {
        removeCheckBoxValuesByValue({
          name: updatedFacetName,
          value: customValue,
        });
      } else {
        removeStoreCheckboxValuesByValue(updatedFacetName, customValue);

        // need to refocus after checkbox is updated.
        focusActiveDropdown();
      }
    }

    return selectedValues;
  };

  const validateRange = (minVal, maxVal) => {
    const minValNumber = parseInt(minVal, 10);
    const maxValNumber = parseInt(maxVal, 10);
    return [
      [!minVal || minValNumber < 1, 'Please enter Min value from 1 to 9998'],
      [!maxVal || maxValNumber > 9999, 'Please enter Max value from 2 to 9999'],
      [minValNumber >= maxValNumber, 'Max value has to be greater than Min value'],
    ].find((error) => error[0]);
  };

  const addCustomRange = (values) => {
    let { min, max } = values;
    let { minVal, maxVal } = customRange.value;
    /* istanbul ignore else */
    if (!min && max > 1) {
      min = '1';
      minVal = '1';
    }
    const name = facetName.value;

    const validationError = validateRange(min, max);
    if (validationError) {
      customForm.value.setErrors({
        min: validationError[1],
      });
    } else {
      const rangeString = `${minVal}|${maxVal}`;
      const customRangeCheckbox = {
        label: `${globalOptions.drawerOptions.value.priceSymbol ? `Custom: $${minVal} - $${maxVal}` : `Custom: ${minVal} - ${maxVal}`}`,
        value: rangeString,
      };

      const facet = isSlideoutMode.value && !props.isDynamic ? facetByName(name) : facetData.value;
      const hasCustomRange = facet.values.some(isCustomOption);
      const selectedValues = [rangeString];
      const selectedCustomValue = hasCustomRange ? facet.selectedValues[0] : null;

      if (isSlideoutMode.value && !props.isDynamic) {
        /* istanbul ignore else */
        if (hasCustomRange) {
          removeCheckBoxValuesByValue({ name, value: selectedCustomValue });
        }

        addCheckboxValue({
          name,
          newValue: customRangeCheckbox,
        });

        updateFacet({ name, selectedValues });
      } else {
        /* istanbul ignore else */
        if (hasCustomRange) {
          removeFacetCheckboxValuesByValue(selectedCustomValue);
        }

        addFacetCheckboxValues(customRangeCheckbox);

        // remove all selected items and use custom range
        replaceFacetSelectedValues({ name, selectedValues });
      }

      customRange.value = {
        minVal: '',
        maxVal: '',
      };
      customForm.value.resetForm();
    }
  };

  const onSubmit = ($event) => addCustomRange($event);

  // TODO use or remove ince atomic-ui is updated
  /* istanbul ignore next  */
  const onCustomRangeFieldUpdate = (modelValue) => {
    const maxLength = globalOptions.maxCustomRangeValueLength.value;
    const value = typeof modelValue === 'number' ? modelValue.toString() : '';
    /* istanbul ignore else */
    if (value.length > maxLength) {
      nextTick(() => {
        const { minVal, maxVal } = customRange.value;
        /* istanbul ignore else */
        if (minVal.length > maxLength) {
          customRange.value.minVal = minVal.substring(0, maxLength);
        }
        /* istanbul ignore else */
        if (maxVal.length > maxLength) {
          customRange.value.maxVal = maxVal.substring(0, maxLength);
        }
      });
    }
  };

  return {
    onSubmit,
    customRange,
    customForm,
    minInput,
    maxInput,
    onUpdate,
    customValueRenderer,
    onCustomRangeFieldUpdate,
    addCustomRange,
  };
}
